/* @flow */
import parse from "csv-parse";
import sleep from "nx-components/utils/sleep";
import Platform from "nexia_mobile/platform";
import { history } from "nexia_mobile/history";

// Hardcoded connection credentials for 724 thermostat (Join Mode)
export const SSID = "NEXIA724";

const HOST = "http://172.31.197.1";

// Change this to true to test 724 wizards in the browser
const stub = false;

export function is724ConfigurationPossible(): boolean {
  if (stub) {
    return true;
  }

  return Platform.canUseNativeHTTP() && Platform.canDo724OpenAuthEncryption();
}

export function currentlyConfiguring724() {
  return (
    history &&
    history.location &&
    history.location.pathname &&
    history.location.pathname.startsWith("/app/xr724/")
  );
}

function getSecurity(security: string): "WEP" | "WPA" | "OPEN" {
  if (security.match(/WEP/)) {
    return "WEP";
  } else if (security.match(/WPA/)) {
    return "WPA";
  } else {
    return "OPEN";
  }
}

type WiFiNetwork = {|
  id: number | string,
  ssid: string,
  secure: boolean,
  security: "OPEN" | "WEP" | "WPA",
|};

export function parseWifiNetworksCSV(csv: string): Promise<Array<WiFiNetwork>> {
  return new Promise((resolve, reject) => {
    const commaReplacement = "__-%2C-__";
    const quoteReplacement = "__-%22-__";
    const escapedCSV = csv
      .replace(/\\,/g, commaReplacement)
      .replace(/\\"/g, quoteReplacement);

    parse(escapedCSV, (err, records) => {
      if (err) {
        reject(err);
        return;
      }
      const data = records
        .map((values) =>
          values.map((value) =>
            value
              .replace(new RegExp(commaReplacement, "g"), ",")
              .replace(new RegExp(quoteReplacement, "g"), '"')
          )
        )
        .map((values) => {
          if (values.length !== 5) {
            throw new Error("724 provided unexpected WiFi CSV format");
          }
          return values;
        })
        .map(([id, rssi, ssid, capabilities, security]) => ({
          id: `${id}-${rssi}-${ssid}-${capabilities}`,
          ssid,
          secure:
            security
              .trim()
              .split(" ")
              .filter((securityTypes) => securityTypes.length).length > 0,
          security: getSecurity(security.trim()),
          rssi,
        }))
        .sort(({ rssi: rssiOne }, { rssi: rssiTwo }) => {
          if (rssiOne > rssiTwo) {
            return 1;
          } else if (rssiOne < rssiTwo) {
            return -1;
          } else {
            return 0;
          }
        })
        .map(({ id, ssid, secure, security }) => ({
          id,
          ssid,
          secure,
          security,
          // Strip out rssi
        }));
      resolve(data);
    });
  });
}

export function getVisibleWifiNetworks(): Promise<Array<WiFiNetwork>> {
  if (stub) {
    return sleep(300).then(() => [
      { id: "1", ssid: "Network 1 (Open)", secure: false, security: "OPEN" },
      { id: "2", ssid: "Network 2 (WEP)", secure: true, security: "WEP" },
      { id: "3", ssid: "Network 3 (WPA/WPA2)", secure: true, security: "WPA" },
    ]);
  }

  return Promise.race([
    Platform.nativeHTTPRequest({ method: "GET", url: `${HOST}/ssid2.txt` })
      .then((response) => response.body)
      .then(parseWifiNetworksCSV),
    // If we can't talk to the thermostat within 10 seconds, we are probably not connected. Reject
    new Promise((resolve, reject) => setTimeout(reject, 10000)),
  ]);
}

function createSessionKey(userKey) {
  // TODO: this should change before shipping
  const randPad = "123123";
  return userKey + randPad;
}

function sendEncrypted(userKey: string, text: string): Promise<void> {
  if (userKey.length !== 10) {
    return Promise.reject(new Error("User key must be 10 bytes"));
  }

  const sessionKey = createSessionKey(userKey);
  return Platform.encryptStringFor724OpenAuth(sessionKey, text)
    .then((encrypted) => {
      return Platform.nativeHTTPRequest({
        method: "GET",
        url: `${HOST}/output.cgi?text=${encrypted}&submit=Submit`,
      });
    })
    .then(() => {
      /* discard response */
    });
}

export function verifyConnectionKey(userKey: string): Promise<boolean> {
  if (stub) {
    return sleep(400).then(() => {
      return userKey !== "0000000000";
    });
  }

  return sendEncrypted(userKey, "handshake")
    .then(() => sleep(1000)) // Seems like the stat takes a bit to write the handshake_status.txt file
    .then(() => {
      return Platform.nativeHTTPRequest({
        method: "GET",
        url: `${HOST}/handshake_status.txt`,
      });
    })
    .then(({ body }) => {
      return /^pass:/.test(body);
    });
}

export function setConnectionPinForNexia(
  pin: string,
  userKey: string
): Promise<void> {
  if (stub) {
    return sleep(400);
  }

  return Promise.race([
    sendEncrypted(userKey, `NEXIA:${pin}`),
    // needs to time out if thermostat is inaccessible. nativeHTTPRequest "GET", doesn't
    // have any way of specifying timeout, so we just reject after 10 seconds
    new Promise((resolve, reject) => setTimeout(reject, 10000)),
  ]);
}

// This will turn off AdHoc mode on the 724, and then the 724 will connect to the provided
// WiFI access point, and then the 724 will try to authenticate with Nexia using the pin it
// was provided by setConnectionPinForNexia. So you should call setConnectionPinForNexia before
// you call this.
export function disableAdHocAndConnectToNexiaUsingSSIDAndPassword(
  ssid: string,
  password: string = "",
  security: "OPEN" | "WEP" | "WPA",
  userKey: string
): Promise<void> {
  if (!ssid) {
    return Promise.reject(
      new Error("You must provide an SSID for the thermostat to connect to")
    );
  }
  if (security == null) {
    return Promise.reject(
      new Error(
        "You must provide an auth mode with which to connect to the WiFi network"
      )
    );
  }

  if (stub) {
    return sleep(2000);
  }

  return new Promise((resolve) => {
    sendEncrypted(userKey, `SSID:${ssid}`)
      .then(() => sendEncrypted(userKey, `PWD:${password}`))
      .then(() => sendEncrypted(userKey, "DHCP:On"))
      .then(() => sendEncrypted(userKey, `SECURITY:${security}`))
      .then(() => sendEncrypted(userKey, "RESTART:now"));
    // Due to a design flaw in the 724, the above fetch will never get a response.
    // The best we can do is assume it worked...
    setTimeout(resolve, 10000);
  });
}



// WEBPACK FOOTER //
// ./src/api/xr724.js